<script setup>
import { router } from '@inertiajs/vue3';
import LoadingSpinner from '@js/components/LoadingSpinner.vue';
import { formatNumber } from '@js/utils/formatter';
import {
    TabsContent,
    TabsIndicator,
    TabsList,
    TabsRoot,
    TabsTrigger,
} from 'reka-ui';
import { computed, inject, nextTick, onMounted, ref, watch } from 'vue';

/**
 * @typedef {Object} Tab
 * @property {object} [icon] - Optional icon component to display next to the title.
 * @property {string} title - The title of the tab.
 * @property {number} [total] - Optional total count to display next to the title.
 * @property {number} [alertCount] - Optional count of rows still waiting on somebody, shown as a
 *                                   badge. Distinct from the total, which counts everything the tab
 *                                   holds.
 * @property {string} [alertTitle] - What that badge counts, since two numbers on one tab say nothing
 *                                   about how they differ.
 * @property {string} [contentSlotName] - Optional name of the slot for tab content.
 * @property {Object.<string, Tab>} [items] - Optional sub-tabs.
 * @property {Object} [content] - Optional component to render as tab content.
 * @property {boolean} [forceMount] - Optional flag to force mount the tab content.
 */
const {
    tabs,
    unmountOnHide = true,
    loading = false,
    syncUrl = true,
} = defineProps({
    tabs: {
        type: Object,
        required: true,
    },
    unmountOnHide: {
        type: Boolean,
        required: false,
    },
    loading: {
        type: Boolean,
        required: false,
    },
    syncUrl: {
        type: Boolean,
        required: false,
    },
});

const firstLoading = ref(true);

const selectedTab = defineModel('selectedTab', { required: false });
const selectedSubTab = ref(null);

const currentTab = computed(() => {
    return tabs[selectedTab.value] ?? {};
});

/**
 * A tab may be dropped while it is the selected one, by a count that has fallen to zero or a set that
 * came back smaller. The selection has to move, or it names a tab nothing can render.
 */
watch(
    () => Object.keys(tabs).join('|'),
    () => {
        const [firstTab] = Object.keys(tabs);

        // A set that came back empty has nothing to fall back to, and naming no tab is not a selection.
        if (
            firstTab !== undefined &&
            selectedTab.value !== undefined &&
            !tabs[selectedTab.value]
        ) {
            update(firstTab);
        }
    },
);

function updateUrl() {
    if (!syncUrl) {
        return;
    }

    nextTick(() => {
        const url = new URL(window.location.href);

        url.searchParams.set('tab', selectedTab.value);

        if (selectedSubTab.value) {
            url.searchParams.set('subTab', selectedSubTab.value);
        } else {
            url.searchParams.delete('subTab');
        }

        for (let key of url.searchParams.keys()) {
            if (key !== 'tab' && key !== 'subTab') {
                url.searchParams.delete(key);
            }
        }

        router.visit(url.toString(), {
            preserveState: true,
            replace: true,
        });
    });
}

function update(value) {
    firstLoading.value = true;

    selectedTab.value = value;
    selectedSubTab.value = null;

    updateUrl();

    firstLoading.value = false;
}

function updateSubTab(value) {
    selectedSubTab.value = value;

    updateUrl();
}

function openNewTab(event, value, subValue) {
    event.preventDefault();

    let url = new URL(window.location.href);
    url.searchParams.set('tab', value);

    if (subValue) {
        url.searchParams.set('subTab', subValue);
    }

    window.open(url.toString(), '_blank');
}

const isInDialog = inject('isInDialog', false);

onMounted(() => {
    if (selectedTab.value === undefined) {
        selectedTab.value = Object.keys(tabs)[0];
    }

    if (syncUrl) {
        const params = new URLSearchParams(window.location.search);
        const tabValue = params.get('tab');
        const subTabValue = params.get('subTab');

        if (tabValue && tabs[tabValue]) {
            selectedTab.value = tabValue;

            if (
                subTabValue &&
                tabs[tabValue].items &&
                tabs[tabValue].items[subTabValue]
            ) {
                selectedSubTab.value = subTabValue;
            }

            updateUrl();
        }
    }

    firstLoading.value = false;
});
</script>

<template>
    <LoadingSpinner v-if="firstLoading" />

    <template v-else>
        <LoadingSpinner v-if="loading" />

        <TabsRoot
            v-model="selectedTab"
            class="flex flex-col"
            @update:modelValue="(value) => update(value)"
            :unmount-on-hide="unmountOnHide"
            :class="{
                hidden: loading,
            }"
        >
            <TabsList
                class="relative flex gap-2 overflow-x-auto overflow-y-hidden border-gray-200"
                :class="{
                    'border-b': !('items' in currentTab),
                    'sticky top-0 z-10 bg-white px-4': isInDialog,
                }"
            >
                <TabsIndicator
                    class="transition-width absolute bottom-0 left-0 h-0.75 w-(--reka-tabs-indicator-size) translate-x-(--reka-tabs-indicator-position) translate-y-px overflow-hidden rounded-t-full transition-all duration-150"
                >
                    <div class="mx-auto h-full w-[90%] bg-indigo-600" />
                </TabsIndicator>

                <TabsTrigger
                    v-for="(tab, identifier) in tabs"
                    :key="identifier"
                    :value="identifier"
                    class="focus:outline-0.75 rounded-t-md px-4 py-3 whitespace-nowrap text-gray-500 hover:text-indigo-600 focus:outline-indigo-600 data-[state=active]:font-medium data-[state=active]:text-indigo-600"
                    @click.ctrl="(event) => openNewTab(event, identifier, null)"
                    @mousedown.middle="
                        (event) => openNewTab(event, identifier, null)
                    "
                >
                    <component
                        v-if="tab.icon"
                        :is="tab.icon"
                        class="mr-1 inline size-4"
                    />

                    {{ tab.title }}

                    <template v-if="'total' in tab">
                        ({{ formatNumber(tab.total, false) }})
                    </template>

                    <!-- Raised off the baseline: a count on the label, not a word beside it. -->
                    <span
                        v-if="tab.alertCount > 0"
                        :title="tab.alertTitle"
                        class="relative -top-1.5 ml-1.5 inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full border border-white bg-yellow-500 px-1 align-middle text-xs text-white"
                    >
                        {{ formatNumber(tab.alertCount, false) }}
                    </span>
                </TabsTrigger>
            </TabsList>

            <TabsContent
                v-for="(tab, identifier) in tabs"
                :key="identifier"
                :value="identifier"
                :class="{
                    'mt-4': !tab.items,
                    'mt-0!': isInDialog,
                    'data-[state=inactive]:hidden': tab.forceMount,
                }"
                :force-mount="'forceMount' in tab ? tab.forceMount : false"
            >
                <div
                    v-if="$slots['content-header']"
                    :class="isInDialog ? 'px-4 pt-4' : 'mb-4'"
                >
                    <slot name="content-header" />
                </div>

                <TabsRoot
                    v-if="tab.items"
                    :model-value="
                        selectedSubTab && tab.items[selectedSubTab]
                            ? selectedSubTab
                            : Object.keys(tab.items)[0]
                    "
                    @update:modelValue="(value) => updateSubTab(value)"
                    :unmount-on-hide="unmountOnHide"
                >
                    <TabsList
                        class="flex gap-4 overflow-x-auto overflow-y-hidden rounded-sm border border-gray-200 bg-white px-4 py-2"
                    >
                        <TabsTrigger
                            v-for="(subTab, subIdentifier) in tab.items"
                            :key="subIdentifier"
                            :value="subIdentifier"
                            class="rounded-md px-3 py-1 text-sm whitespace-nowrap duration-150 hover:bg-indigo-600 hover:text-white data-[state=active]:bg-indigo-600 data-[state=active]:text-white"
                        >
                            {{ subTab.title }}

                            <template v-if="'total' in subTab">
                                ({{ formatNumber(subTab.total, false) }})
                            </template>
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent
                        v-for="(subTab, subIdentifier) in tab.items"
                        :key="subIdentifier"
                        :value="subIdentifier"
                        class="mt-4"
                        :class="{
                            'data-[state=inactive]:hidden': subTab.forceMount,
                        }"
                        :force-mount="
                            'forceMount' in tab ? subTab.forceMount : false
                        "
                    >
                        <template v-if="subTab.contentSlotName">
                            <slot :name="subTab.contentSlotName" />
                        </template>

                        <component v-else :is="subTab.content" />
                    </TabsContent>
                </TabsRoot>

                <template v-else-if="tab.contentSlotName">
                    <slot :name="tab.contentSlotName" />
                </template>

                <component v-else :is="tab.content" />
            </TabsContent>
        </TabsRoot>
    </template>
</template>
